import LoadingAnimate from '@/components/loading-animate'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { autocompleteOrganizationOrganizationIdUsersAutocompleteGetOptions } from '@/generated/@tanstack/react-query.gen'
import { useDebounce } from '@/hooks/use-debounce'
import { useRefSize } from '@/hooks/use-ref-size'
import { cn } from '@/lib/utils'
import { useUser } from '@/pages/enterprise/user-provider'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover'
import { useQuery } from '@tanstack/react-query'
import { CommandEmpty } from 'cmdk'
import { isEmpty } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'

interface ISearchAutocompletedProps {
  value: string | null
  onChange: (value: string) => void
}

export default function SearchAutocompleted({
  value,
  onChange,
}: ISearchAutocompletedProps) {
  const { organizationId } = useUser()
  const [search, setSearch] = useState(value ?? '')
  const searchDebouce = useDebounce(search, 1000)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const ref = useRef<HTMLButtonElement>(null)
  const { width } = useRefSize(ref)

  const { data, isLoading, error } = useQuery({
    ...autocompleteOrganizationOrganizationIdUsersAutocompleteGetOptions({
      path: {
        organization_id: organizationId,
      },
      query: {
        search: searchDebouce,
      },
    }),
    enabled: Boolean(searchDebouce),
    select: (v) => v,
  })

  // implement when parent reset value then reset search also
  useEffect(() => {
    if (value === null || value === '') {
      setSearch('')
    }
  }, [value])

  const handleSelect = ({ email, name }: { email: string; name: string }) => {
    onChange(email)
    setSearch(name)
    setOpen(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSelect({ email: search, name: search }) // search is the email here
    }
    if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <Popover open={true} onOpenChange={(v) => setOpen(v)}>
      <PopoverTrigger ref={ref} className="!p-0 w-full md:w-fit">
        <div className="relative h-9 w-full md:w-[253px]">
          <div className="pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center pl-4">
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.5">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M9.69353 12.5352C12.4234 11.3751 13.6959 8.2216 12.5357 5.49177C11.3755 2.76193 8.22208 1.48945 5.49225 2.6496C2.76241 3.80975 1.48993 6.96321 2.65008 9.69305C3.81024 12.4229 6.9637 13.6954 9.69353 12.5352Z"
                  stroke="black"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M11.3903 11.3896L15.5556 15.5556"
                  stroke="black"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
            </svg>
          </div>
          <Input
            ref={inputRef}
            className={cn(
              ` bg-white !pl-10 h-[38px] placeholder:text-black/50  rounded-[19px]
                shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:!border-input 
                focus-visible:ring-input override-border-input 
              `,
              {
                'rounded-b-none border-input': open,
              }
            )}
            placeholder="Search user"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setOpen(true)
            }}
            onKeyDown={handleKeyDown}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn('z-50 bg-white mt-0 overflow-hidden', {
          'border border-input rounded-b-[19px]': open,
        })}
        sticky="always"
        style={{ width: width }}
      >
        {open && (
          <Command
            className={cn({
              'pb-4': !isLoading && !!data,
            })}
            onSubmit={(e) => e.preventDefault()}
          >
            <CommandEmpty>
              {!isLoading && isEmpty(data) && (
                <div className="h-10 flex items-center justify-center">
                  <p className="text-center text-sm">
                    No matching users found.
                  </p>
                </div>
              )}
            </CommandEmpty>
            <CommandList className="overflow-y-auto max-h-[30vh] scrollbar-hide">
              {isLoading && (
                <div className="w-full flex items-center justify-center h-10 p-4">
                  <LoadingAnimate iconClassName="w-10" title="Loading..." />
                </div>
              )}
              {error && <div>Error: {(error as Error).message}</div>}
              {!isLoading && !isEmpty(data) && (
                <CommandGroup>
                  {data?.map((suggestion) => (
                    <CommandItem
                      className="cursor-pointer flex flex-col justify-start text-left gap-0 rounded-lg py-2 px-4 hover:bg-[#F2F2F2]"
                      key={suggestion.email}
                      onSelect={() =>
                        handleSelect({
                          email: suggestion.email,
                          name: suggestion.fullname,
                        })
                      }
                    >
                      <div className="text-left w-full">
                        {suggestion.fullname
                          .split(new RegExp(`(${search})`, 'gi'))
                          .map((part, index) => (
                            <span
                              key={index}
                              className={
                                part.toLowerCase() === search.toLowerCase()
                                  ? 'font-bold'
                                  : ''
                              }
                            >
                              {part}
                            </span>
                          ))}
                      </div>
                      <div className="text-left w-full text-xs text-muted-foreground">
                        {suggestion.email
                          .split(new RegExp(`(${search})`, 'gi'))
                          .map((part, index) => (
                            <span
                              key={index}
                              className={
                                part.toLowerCase() === search.toLowerCase()
                                  ? 'font-bold'
                                  : ''
                              }
                            >
                              {part}
                            </span>
                          ))}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}
